import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Radio,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import SearchRounded from "@mui/icons-material/SearchRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";
import ReorderRounded from "@mui/icons-material/ReorderRounded";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";

import type { Position, AssetClass, PositionSide } from "@/data/mock-positions";

// ---- Public types + helpers ----------------------------------------------

export type PositionsViewMode = "simple" | "advanced";

/**
 * Preset chips — curated scans for the Positions tab. Kept to a small
 * list so the chip row stays scannable; combining with side /
 * assetClass / sort covers the rest of the useful slices.
 */
export type PositionPreset =
  | "today_gainers"
  | "today_losers"
  | "total_winners"
  | "total_losers"
  | "concentrated"
  | "dust"
  | "recent"
  | "mature";

/** Side filter is a standalone axis (matches Alpaca's All / Long / Short). */
export type PositionSideFilter = "all" | "long" | "short";

/** Asset-class filter. Matches Alpaca's Columns > Asset Class menu. */
export type AssetClassFilter = "all" | AssetClass;

export type PositionSortKey =
  | "marketValue"
  | "todayPct"
  | "todayDollar"
  | "totalPct"
  | "totalDollar"
  | "qty"
  | "costBasis"
  | "avgEntry"
  | "ticker";

export interface PositionsFilterState {
  mode: PositionsViewMode;
  search: string;
  side: PositionSideFilter;
  assetClass: AssetClassFilter;
  /** `null` = no preset active; only one preset can be chosen at a time. */
  preset: PositionPreset | null;
  sortKey: PositionSortKey;
  sortDir: "asc" | "desc";
}

export const DEFAULT_POSITIONS_FILTER: PositionsFilterState = {
  mode: "simple",
  search: "",
  side: "all",
  assetClass: "all",
  preset: null,
  // Biggest positions first — matches the mental model of "what's
  // actually moving my portfolio right now".
  sortKey: "marketValue",
  sortDir: "desc"
};

// ---- Option tables -------------------------------------------------------

const PRESETS: ReadonlyArray<{
  value: PositionPreset;
  label: string;
  build: (positions: Position[]) => (p: Position) => boolean;
}> = [
  {
    value: "today_gainers",
    label: "Today gainers",
    build: () => (p) => p.todayPLPct > 0
  },
  {
    value: "today_losers",
    label: "Today losers",
    build: () => (p) => p.todayPLPct < 0
  },
  {
    value: "total_winners",
    label: "Winners",
    build: () => (p) => p.totalPLDollar > 0
  },
  {
    value: "total_losers",
    label: "Losers",
    build: () => (p) => p.totalPLDollar < 0
  },
  {
    value: "concentrated",
    label: "Top 5 by value",
    // Concentration scan — which few rows dominate the book. Factory
    // pre-computes the allow-list so the predicate is O(1).
    build: (positions) => {
      const allow = new Set(
        [...positions]
          .sort((a, b) => b.marketValue - a.marketValue)
          .slice(0, 5)
          .map((p) => p.id)
      );
      return (p) => allow.has(p.id);
    }
  },
  {
    value: "dust",
    label: "Dust (< $100)",
    build: () => (p) => p.marketValue < 100
  },
  {
    value: "recent",
    label: "Recent (< 30d)",
    build: () => (p) => ageDays(p.openedAt) < 30
  },
  {
    value: "mature",
    label: "Mature (≥ 180d)",
    build: () => (p) => ageDays(p.openedAt) >= 180
  }
];

const SIDE_OPTIONS: ReadonlyArray<{ value: PositionSideFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "long", label: "Long" },
  { value: "short", label: "Short" }
];

const ASSET_CLASS_OPTIONS: ReadonlyArray<{ value: AssetClassFilter; label: string }> = [
  { value: "all", label: "All assets" },
  { value: "stocks", label: "Stocks" },
  { value: "etf", label: "ETFs" },
  { value: "crypto", label: "Crypto" },
  { value: "options", label: "Options" }
];

const SORT_OPTIONS: ReadonlyArray<{ value: PositionSortKey; label: string }> = [
  { value: "marketValue", label: "Market value" },
  { value: "todayPct", label: "Today %" },
  { value: "todayDollar", label: "Today $" },
  { value: "totalPct", label: "Total %" },
  { value: "totalDollar", label: "Total $" },
  { value: "qty", label: "Quantity" },
  { value: "costBasis", label: "Cost basis" },
  { value: "avgEntry", label: "Avg entry" },
  { value: "ticker", label: "Ticker (A–Z)" }
];

// ---- Pure filter + sort --------------------------------------------------

/**
 * Apply search + side + asset class + preset + sort. Pure — consumers
 * lift the filter state and memoise with `useMemo` keyed on
 * `[positions, filter]`.
 */
export function applyPositionsFilter(
  positions: Position[],
  filter: PositionsFilterState
): Position[] {
  const q = filter.search.trim().toLowerCase();

  let out = positions.filter((p) => {
    // Search — ticker OR instrument name. Case-insensitive.
    if (q) {
      const matches =
        p.asset.toLowerCase().includes(q) ||
        p.assetName.toLowerCase().includes(q);
      if (!matches) return false;
    }
    // Side filter.
    if (filter.side !== "all" && p.side !== (filter.side as PositionSide)) {
      return false;
    }
    // Asset-class filter.
    if (filter.assetClass !== "all" && p.assetClass !== filter.assetClass) {
      return false;
    }
    return true;
  });

  // Preset predicate layered on top — built from the already-narrowed
  // cohort so "Top 5 by value" respects the other filters.
  if (filter.preset) {
    const spec = PRESETS.find((p) => p.value === filter.preset);
    if (spec) {
      const pred = spec.build(out);
      out = out.filter(pred);
    }
  }

  // Sort by the selected key. Secondary sort by ticker keeps
  // deterministic ordering on ties.
  const dir = filter.sortDir === "asc" ? 1 : -1;
  out.sort((a, b) => {
    const av = extractSortValue(a, filter.sortKey);
    const bv = extractSortValue(b, filter.sortKey);
    if (av === bv) return a.asset.localeCompare(b.asset);
    return av < bv ? -1 * dir : 1 * dir;
  });
  return out;
}

function extractSortValue(p: Position, key: PositionSortKey): number | string {
  switch (key) {
    case "marketValue":
      return p.marketValue;
    case "todayPct":
      return p.todayPLPct;
    case "todayDollar":
      return p.todayPLDollar;
    case "totalPct":
      return p.totalPLPct;
    case "totalDollar":
      return p.totalPLDollar;
    case "qty":
      return p.qty;
    case "costBasis":
      return p.costBasis;
    case "avgEntry":
      return p.avgEntryPrice;
    case "ticker":
      // Return the ticker string directly; the compare branch above
      // uses `<` which does lexicographic string compare as expected.
      return p.asset;
  }
}

function ageDays(iso: string): number {
  const opened = new Date(iso + "T00:00:00Z").getTime();
  return Math.max(0, Math.floor((Date.now() - opened) / 86400_000));
}

/**
 * Shared sx for selectable chips in this bar (asset-class + presets).
 *
 * Why a bespoke helper instead of MUI's `variant="filled" color="primary"`:
 * In the app's dark theme the primary palette is saturated green — MUI
 * defaults to a filled green background with auto-picked contrastText
 * which also comes out greenish, giving green-on-green that reads as
 * "disabled" at small chip sizes. Flipping to `alpha(primary.main, 0.18)`
 * bg + `primary.main` text (the same pattern used by BotsFilterBar's
 * PresetChip) fixes the contrast while keeping the "this is the active
 * filter" colour cue.
 *
 * The unselected style (`action.hover` bg + `text.primary`) matches the
 * tone of MUI's outlined chip but without a border, so active and
 * inactive chips have the same silhouette — only the fill changes.
 */
function selectedChipSx(active: boolean): SxProps<Theme> {
  return {
    flexShrink: 0,
    borderRadius: 999,
    px: 0.5,
    fontWeight: 500,
    // Apr-2026 typography sweep: body1 (1rem) + proportional height
    // bump — chips were markedly smaller than the Simple/Advanced
    // toggle and Buying Power row, which made them read as secondary.
    fontSize: "1rem",
    height: 36,
    bgcolor: (t: Theme) =>
      active
        ? alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.18 : 0.12)
        : t.palette.action.hover,
    color: active ? "primary.main" : "text.primary",
    "& .MuiChip-deleteIcon": {
      color: active ? "primary.main" : "text.secondary"
    },
    "& .MuiChip-icon": {
      color: active ? "primary.main" : "text.secondary"
    },
    "&:hover": {
      bgcolor: (t: Theme) =>
        active
          ? alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.26 : 0.2)
          : t.palette.action.selected
    }
  };
}

// ---- Component -----------------------------------------------------------

interface PositionsFilterBarProps {
  filter: PositionsFilterState;
  onChange: (next: PositionsFilterState) => void;
  sx?: SxProps<Theme>;
}

/**
 * Filter bar for the Positions tab — layout mirrors BotsFilterBar /
 * OrdersFilterBar (Apr-2026 polish round). Single always-visible row:
 *
 *   [search ………………] [Simple] [Advanced] [Tune]
 *
 * Tune toggles a Collapse panel containing:
 *   • Side segmented (All / Long / Short)
 *   • Asset class chip (opens a single-select menu)
 *   • Preset chip rail, radio-style
 *   • Sort chip — opens a single-select menu of sort keys, taps on
 *     the active key flip direction
 *
 * The chip rail auto-discloses whenever any filter is active so the
 * user can see what's narrowing their results without clicking Tune.
 * Tap-while-open Tune resets every facet to defaults — same shortcut
 * the Bots / Orders bars use.
 */
export function PositionsFilterBar({ filter, onChange, sx }: PositionsFilterBarProps) {
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
  const [assetClassAnchor, setAssetClassAnchor] = useState<HTMLElement | null>(null);

  // Tune disclosure mirrors the Orders/Bots tab: chip rail starts collapsed,
  // auto-opens whenever a filter is active. Tap-while-open resets the filter
  // back to defaults (preserves view mode — Simple/Advanced is a layout pref,
  // not a filter).
  const anyFilterActive =
    filter.search.trim().length > 0 ||
    filter.side !== DEFAULT_POSITIONS_FILTER.side ||
    filter.assetClass !== DEFAULT_POSITIONS_FILTER.assetClass ||
    filter.preset !== null ||
    filter.sortKey !== DEFAULT_POSITIONS_FILTER.sortKey ||
    filter.sortDir !== DEFAULT_POSITIONS_FILTER.sortDir;
  const [filtersOpen, setFiltersOpen] = useState<boolean>(anyFilterActive);
  useEffect(() => {
    if (anyFilterActive && !filtersOpen) setFiltersOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyFilterActive]);

  function toggleFiltersDisclosure() {
    if (filtersOpen) {
      setFiltersOpen(false);
      onChange({
        ...DEFAULT_POSITIONS_FILTER,
        mode: filter.mode
      });
    } else {
      setFiltersOpen(true);
    }
  }

  const sortLabel = useMemo(() => {
    const opt = SORT_OPTIONS.find((o) => o.value === filter.sortKey);
    const base = opt ? opt.label : "Sort";
    return `${base} ${filter.sortDir === "desc" ? "↓" : "↑"}`;
  }, [filter.sortKey, filter.sortDir]);

  const assetClassLabel = useMemo(() => {
    const opt = ASSET_CLASS_OPTIONS.find((o) => o.value === filter.assetClass);
    return opt ? opt.label : "All assets";
  }, [filter.assetClass]);

  const togglePreset = (p: PositionPreset) => {
    onChange({ ...filter, preset: filter.preset === p ? null : p });
  };

  const pickSort = (key: PositionSortKey) => {
    if (filter.sortKey === key) {
      onChange({
        ...filter,
        sortDir: filter.sortDir === "asc" ? "desc" : "asc"
      });
    } else {
      const defaultDir: "asc" | "desc" = key === "ticker" ? "asc" : "desc";
      onChange({ ...filter, sortKey: key, sortDir: defaultDir });
    }
    setSortAnchor(null);
  };

  return (
    <Box sx={sx}>
      {/* Single always-visible header row: search + view-toggle +
       *  filter disclosure. Mirrors Bots/Orders exactly so the three
       *  tabs feel like one product. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ mb: filtersOpen ? 1.25 : 0 }}
      >
        <TextField
          size="small"
          placeholder="Search ticker or name"
          value={filter.search}
          onChange={(e) => onChange({ ...filter, search: e.target.value })}
          // Focusing search auto-discloses the filter rail so search +
          // facets surface together — matches Bots/Orders pattern.
          onFocus={() => {
            if (!filtersOpen) setFiltersOpen(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: filter.search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onChange({ ...filter, search: "" })}
                  sx={{ p: 0.25 }}
                >
                  <CloseRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: {
              borderRadius: 2,
              bgcolor: (t) => t.palette.action.hover,
              "& fieldset": { border: "none" },
              fontSize: "1rem",
              height: 44
            }
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />

        {/* Simple view (3 horizontal bars) */}
        <Tooltip title="Simple view">
          <IconButton
            size="small"
            aria-label="Simple view"
            onClick={() => {
              if (filter.mode !== "simple") onChange({ ...filter, mode: "simple" });
            }}
            sx={{
              color: filter.mode === "simple" ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filter.mode === "simple" ? t.palette.action.selected : "transparent"
            }}
          >
            <ReorderRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        {/* Advanced (3 vertical columns) */}
        <Tooltip title="Advanced (table) view">
          <IconButton
            size="small"
            aria-label="Advanced view"
            onClick={() => {
              if (filter.mode !== "advanced") onChange({ ...filter, mode: "advanced" });
            }}
            sx={{
              color: filter.mode === "advanced" ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filter.mode === "advanced" ? t.palette.action.selected : "transparent"
            }}
          >
            <ViewColumnRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        {/* Tune — disclose / hide chip rail. Tap-while-open also resets
         *  filters back to defaults (preserves Simple/Advanced choice). */}
        <Tooltip title={filtersOpen ? "Hide and clear filters" : "Show filters"}>
          <IconButton
            size="small"
            aria-label={filtersOpen ? "Hide and clear filters" : "Show filters"}
            onClick={toggleFiltersDisclosure}
            sx={{
              color:
                filtersOpen || anyFilterActive ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filtersOpen ? t.palette.action.selected : "transparent"
            }}
          >
            <TuneRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={filtersOpen} timeout={200} unmountOnExit>
        {/* Side segmented + Asset-class chip — same scroll/scrollbar-
         *  hide treatment as the chip rail below. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            mb: 1,
            overflowX: "auto",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={filter.side}
            onChange={(_, v: PositionSideFilter | null) => {
              if (!v) return;
              onChange({ ...filter, side: v });
            }}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                height: 36,
                px: 1.5,
                py: 0.25
              }
            }}
          >
            {SIDE_OPTIONS.map((o) => (
              <ToggleButton key={o.value} value={o.value}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Chip
            label={assetClassLabel}
            size="small"
            onClick={(e) => setAssetClassAnchor(e.currentTarget)}
            onDelete={
              filter.assetClass !== "all"
                ? () => onChange({ ...filter, assetClass: "all" })
                : undefined
            }
            deleteIcon={<CloseRounded fontSize="small" />}
            icon={<ArrowDropDownRounded />}
            sx={selectedChipSx(filter.assetClass !== "all")}
          />
          <Menu
            anchorEl={assetClassAnchor}
            open={!!assetClassAnchor}
            onClose={() => setAssetClassAnchor(null)}
          >
            {ASSET_CLASS_OPTIONS.map((o) => (
              <MenuItem
                key={o.value}
                selected={filter.assetClass === o.value}
                onClick={() => {
                  onChange({ ...filter, assetClass: o.value });
                  setAssetClassAnchor(null);
                }}
              >
                <Radio
                  checked={filter.assetClass === o.value}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        {/* Preset chips + Sort chip */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            overflowX: "auto",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}
        >
          {PRESETS.map((p) => {
            const selected = filter.preset === p.value;
            return (
              <Chip
                key={p.value}
                label={p.label}
                size="small"
                onClick={() => togglePreset(p.value)}
                sx={selectedChipSx(selected)}
              />
            );
          })}
          <Box sx={{ flex: 1, minWidth: 8 }} />
          <Chip
            label={sortLabel}
            size="small"
            icon={<ArrowDropDownRounded />}
            onClick={(e) => setSortAnchor(e.currentTarget)}
            sx={selectedChipSx(false)}
          />
          <Menu
            anchorEl={sortAnchor}
            open={!!sortAnchor}
            onClose={() => setSortAnchor(null)}
          >
            {SORT_OPTIONS.map((o) => {
              const active = filter.sortKey === o.value;
              return (
                <MenuItem
                  key={o.value}
                  selected={active}
                  onClick={() => pickSort(o.value)}
                >
                  <Radio checked={active} size="small" sx={{ mr: 1 }} />
                  <ListItemText
                    primary={o.label}
                    secondary={
                      active
                        ? filter.sortDir === "asc"
                          ? "Ascending"
                          : "Descending"
                        : undefined
                    }
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </Stack>
      </Collapse>
    </Box>
  );
}
